/**
 *  @class
 *  @function PredictiveSearch
 */
class PredictiveSearch {
  constructor() {
    this.container = document.querySelector('.thb-quick-search');
    // Loaded on every page, but not every page renders quick-search markup
    // (the password page, and header styles without it). Bail instead of
    // throwing on the first querySelector of a null container.
    if (!this.container) return;

    this.button = document.getElementById('quick-search');
    this.close_button = this.container.querySelector('.thb-search-close');
    this.input = this.container.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.container.querySelector('.thb-quick-search--results');
    this.cache = new Map();
    this.controller = null;

    this.setupEventListeners();
  }

  // The header wrapper is absent on some templates; every caller went through
  // an unguarded document.querySelector before.
  get headerSection() {
    return document.querySelector('.header-section');
  }

  setupEventListeners() {
    const form = this.container.querySelector('form.searchform');
    if (form) form.addEventListener('submit', this.onFormSubmit.bind(this));

    if (this.input) {
      this.input.addEventListener('input', debounce((event) => {
        this.onChange(event);
      }, 300).bind(this));
    }

    if (this.button) this.button.addEventListener('click', (event) => {
      event.preventDefault();
      const header = this.headerSection;
      if (!header) return;
      header.classList.toggle('search-open');

      if (header.classList.contains('search-open')) {
        setTimeout(() => {
          this.input.focus({
            preventScroll: true
          });
        }, 100);
        dispatchCustomEvent('search:open');
      }
    });

    // Close.
    this.close_button.addEventListener('click', (event) => {
      this.close();
      event.preventDefault();
    });
    document.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') {
        this.close();
      }
    });
  }

  categoryToggle() {
    this.category_toggle = this.container.querySelectorAll('.search-categories a');

    this.category_toggle.forEach((link) => {
      link.addEventListener('click', (event) => {
        [].forEach.call(this.category_toggle, function (el) {
          el.classList.remove('active');
        });
        link.classList.add('active');
        let target = link.getAttribute('href');
        this.container.querySelectorAll('.search-results').forEach((section) => {
          section.classList.remove('active');
        });
        this.container.querySelector(target).classList.add('active');

        event.preventDefault();
      });
    });
  }

  getQuery() {
    // Lowercased for the same reason as the submit handler at the foot of this
    // file, so the dropdown and the results page agree on what was searched.
    return this.input.value.trim().toLowerCase();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.predictiveSearchResults.classList.remove('active');
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) {
      event.preventDefault();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    if (this.controller) {
      this.controller.abort();
    }
    this.controller = new AbortController();

    // Serve from cache if available
    if (this.cache.has(searchTerm)) {
      this.renderSearchResults(this.cache.get(searchTerm));
      return;
    }

    this.predictiveSearchResults.classList.add('loading');

    const params = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'product,article,query,page',
      'resources[limit]': '10',
      'resources[options][fields]': 'title,product_type,vendor,variants.title,variants.sku',
      // The Predictive Search API leaves unavailable products out of the
      // response unless asked. On a replacements catalogue that is wrong: the
      // shopper searching for an exact discontinued piece is precisely the one
      // who needs to find it, even when it is sold out. The regular /search
      // endpoint already includes them, so without this the two disagreed.
      'resources[options][unavailable_products]': 'show',
      section_id: 'predictive-search'
    });

    fetch(`${theme.routes.predictive_search_url}?${params.toString()}`, { signal: this.controller.signal })
      .then((response) => {
        this.predictiveSearchResults.classList.remove('loading');
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;

        // Cap cache size
        if (this.cache.size >= 50) {
          const firstKey = this.cache.keys().next().value;
          this.cache.delete(firstKey);
        }
        this.cache.set(searchTerm, resultsMarkup);

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          return;
        }
        this.predictiveSearchResults.classList.remove('loading');
        this.predictiveSearchResults.classList.remove('active');
      });
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;

    this.predictiveSearchResults.classList.add('active');

    this.categoryToggle();
  }

  open() {
    if (this.headerSection) this.headerSection.classList.add('search-open');
  }

  close() {
    if (this.headerSection) this.headerSection.classList.remove('search-open');
  }
}
window.addEventListener('load', () => {
  // Only instantiate where the markup actually exists.
  if (typeof PredictiveSearch !== 'undefined' && document.querySelector('.thb-quick-search')) {
    new PredictiveSearch();
  }
});

/**
 * Search results differ by capitalisation, and not as simple case-folding:
 * measured on this catalogue, "teapot" returns 57, "Teapot" 52 and "TEAPOT" 3.
 * Brand terms are unaffected (lenox/LENOX both 62), so the cause is the search
 * index rather than the theme — but shoppers who type in caps, or whose phone
 * auto-capitalises the first letter, currently get a fraction of the catalogue.
 *
 * Lowercasing the term on submit gives everyone the fullest result set. This is
 * a mitigation, not the cure: the index and the Search & Discovery synonym rules
 * still need looking at in admin.
 */
(function () {
  'use strict';
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form || form.tagName !== 'FORM') return;
    const action = form.getAttribute('action') || '';
    if (action.indexOf('/search') === -1) return;

    const field = form.querySelector('input[name="q"]');
    if (!field || !field.value) return;

    const lowered = field.value.toLowerCase();
    if (lowered !== field.value) field.value = lowered;
  }, true);
})();
